import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhoneInput from '../components/forms/phoneInput';
import { getIntakeInfo, getIntakeLinkInfo, submitIntake, submitIntakeViaLink } from '../services/clientIntakes';
import { SUPPORTED_LANGUAGES } from '../i18n/i18n';

/**
 * Public customer self-entry form — reached from a link the office sends.
 * No login. Two link kinds share this one page:
 *   • app/c/<token>       — the company's reusable sign-up link
 *   • app/c/link/<token>  — a private single-use link minted for one enquiry
 * The customer enters their own contact and address details; the office
 * reviews them under Clients › Pending before they become a client record.
 */

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// API 404/410 messages for a private link → which friendly page to show.
const linkProblemKind = (message) => {
    const m = String(message || '').toLowerCase();
    if (m.includes('expired')) return 'expired';
    if (m.includes('already used')) return 'used';
    if (m.includes('no longer valid')) return 'revoked';
    return 'invalid';
};

const styles = `
  .pi{--blue:#2563EB;--ink:#0F172A;--muted:#64748B;--line:#E2E8F0;--bg:#F8FAFC;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:var(--bg);min-height:100vh;color:var(--ink);-webkit-font-smoothing:antialiased}
  .pi *{box-sizing:border-box}
  .pi .wrap{max-width:640px;margin:0 auto;padding:28px 18px 60px}
  .pi .head{display:flex;align-items:center;gap:14px;margin-bottom:18px}
  .pi .head img{width:52px;height:52px;border-radius:12px;object-fit:cover;border:1px solid var(--line);background:#fff}
  .pi .head .co{font-size:13px;font-weight:700;color:var(--muted);letter-spacing:.04em;text-transform:uppercase}
  .pi .head h1{font-size:22px;font-weight:800;margin:2px 0 0;letter-spacing:-.3px}
  .pi .lang{margin-left:auto;display:flex;gap:4px}
  .pi .lang button{border:1px solid var(--line);background:#fff;border-radius:999px;padding:4px 10px;font:700 12px inherit;color:var(--muted);cursor:pointer}
  .pi .lang button.on{border-color:var(--blue);color:var(--blue)}
  .pi p.intro{color:var(--muted);font-size:14.5px;line-height:1.55;margin:0 0 18px}
  .pi .card{background:#fff;border:1px solid var(--line);border-radius:16px;padding:20px;box-shadow:0 1px 2px rgba(15,23,42,.04)}
  .pi .seg{display:flex;gap:4px;background:var(--bg);border:1px solid var(--line);border-radius:12px;padding:4px;margin-bottom:16px}
  .pi .seg button{flex:1;border:0;background:transparent;border-radius:8px;padding:9px;font:700 13px inherit;color:var(--muted);cursor:pointer}
  .pi .seg button.on{background:#fff;color:var(--ink);box-shadow:0 1px 2px rgba(15,23,42,.08)}
  .pi .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px 14px}
  .pi .grid .full{grid-column:1 / -1}
  @media(max-width:520px){.pi .grid{grid-template-columns:1fr}}
  .pi label{display:flex;flex-direction:column;gap:6px;font-size:12.5px;font-weight:700}
  .pi label small{font-weight:500;color:var(--muted)}
  .pi input,.pi textarea{border:1px solid var(--line);border-radius:10px;padding:11px 12px;font:500 15px inherit;color:var(--ink);outline:0;width:100%;background:#fff}
  .pi input:focus,.pi textarea:focus{border-color:var(--blue);box-shadow:0 0 0 3px rgba(37,99,235,.15)}
  .pi .phone-cell{display:flex;flex-direction:column;gap:6px;font-size:12.5px;font-weight:700}
  .pi .phone-cell .pi-phone{width:100%}
  .pi .sec{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin:18px 0 10px}
  /* display:none, not off-screen: an off-screen field is still "visible" to
     Chrome autofill and to password managers, which happily filled it and got
     the submission silently discarded as a bot. A bot parsing the DOM still
     finds and fills it. */
  .pi .hp{display:none}
  .pi .ts{margin-top:18px;min-height:65px;display:flex;justify-content:center}
  .pi .btn{width:100%;margin-top:18px;border:0;border-radius:12px;padding:14px;background:var(--blue);color:#fff;font:800 15px inherit;cursor:pointer}
  .pi .btn:disabled{opacity:.6;cursor:not-allowed}
  .pi .err{margin-top:12px;padding:10px 12px;border-radius:10px;background:#FEF2F2;color:#B91C1C;font-size:13.5px;font-weight:600}
  .pi .privacy{font-size:12.5px;color:var(--muted);margin-top:14px;line-height:1.5}
  .pi .done{text-align:center;padding:36px 20px}
  .pi .done .ok{width:56px;height:56px;border-radius:50%;background:#DCFCE7;color:#15803D;display:flex;align-items:center;justify-content:center;margin:0 auto 14px}
  .pi .done h2{font-size:22px;margin:0 0 8px}
  .pi .done p{color:var(--muted);margin:0}
`;

const init = {
    clientType: 'INDIVIDUAL', companyName: '', firstName: '', lastName: '', phone: '', email: '',
    street: '', city: '', state: '', zipCode: '', country: 'România', apartmentNo: '', floor: '', intercomName: '', notes: '',
    website: '',
};

// Defined outside the page so its identity is stable across renders — an
// inline component would be remounted on every keystroke and lose focus.
const Field = ({ k, label, type = 'text', required, optional, placeholder, full, as, value, onChange, optionalText }) => (
    <label className={full ? 'full' : ''}>
        <span>{label} {required && <span style={{ color: '#DC2626' }}>*</span>} {optional && <small>({optionalText})</small>}</span>
        {as === 'textarea'
            ? <textarea rows={3} value={value} onChange={onChange(k)} placeholder={placeholder} />
            : <input type={type} value={value} onChange={onChange(k)} required={required} placeholder={placeholder} autoComplete="on" />}
    </label>
);

// Loads the Turnstile script once per page, however many times it's asked.
let turnstileLoader = null;
const loadTurnstile = () => {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoader) {
        turnstileLoader = new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src^="${TURNSTILE_SCRIPT.split('?')[0]}"]`);
            const script = existing || document.createElement('script');
            const onLoad = () => resolve(window.turnstile);
            script.addEventListener('load', onLoad);
            script.addEventListener('error', () => { turnstileLoader = null; reject(new Error('turnstile failed to load')); });
            if (!existing) {
                script.src = TURNSTILE_SCRIPT;
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            } else if (window.turnstile) {
                onLoad();
            }
        });
    }
    return turnstileLoader;
};

/**
 * Turnstile widget. Renders only when a site key is configured; otherwise
 * the API (with no secret key) skips verification too.
 */
const Turnstile = ({ lang, onToken, resetKey }) => {
    const ref = useRef(null);
    const widgetId = useRef(null);
    // Latest callback without re-rendering the widget on every parent render.
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!TURNSTILE_SITE_KEY || !ref.current) return undefined;
        let cancelled = false;
        loadTurnstile().then((ts) => {
            if (cancelled || !ref.current || !ts) return;
            widgetId.current = ts.render(ref.current, {
                sitekey: TURNSTILE_SITE_KEY,
                language: lang,
                callback: (token) => onTokenRef.current(token),
                'expired-callback': () => onTokenRef.current(''),
                'error-callback': () => onTokenRef.current(''),
            });
        }).catch(() => { /* the API will reject without a token; the error is shown then */ });
        return () => {
            cancelled = true;
            if (widgetId.current && window.turnstile) {
                try { window.turnstile.remove(widgetId.current); } catch { /* already gone */ }
            }
            widgetId.current = null;
        };
    }, [lang]);

    // Tokens are single-use: after a failed submit ask for a fresh one.
    useEffect(() => {
        if (resetKey && widgetId.current && window.turnstile) {
            try { window.turnstile.reset(widgetId.current); } catch { /* ignore */ }
            onTokenRef.current('');
        }
    }, [resetKey]);

    if (!TURNSTILE_SITE_KEY) return null;
    return <div className="ts"><div ref={ref} /></div>;
};

const PublicIntakePage = () => {
    const { token } = useParams();
    const { pathname } = useLocation();
    // /c/link/<token> is a private single-use link; /c/<token> the company form.
    const viaLink = /^\/c\/link\//.test(pathname);
    const { t, i18n } = useTranslation();

    const [company, setCompany] = useState(null);
    // null = fine; otherwise 'invalid' | 'expired' | 'used' | 'revoked'
    const [problem, setProblem] = useState(null);
    const [form, setForm] = useState(init);
    const [phoneValid, setPhoneValid] = useState(null);
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileReset, setTurnstileReset] = useState(0);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        let alive = true;
        const fetchInfo = viaLink ? getIntakeLinkInfo : getIntakeInfo;
        fetchInfo(token)
            .then((res) => {
                if (!alive) return;
                if (res?.code === 200) setCompany(res.message);
                else setProblem(viaLink ? linkProblemKind(res?.message) : 'invalid');
            })
            .catch(() => alive && setProblem('invalid'));
        return () => { alive = false; };
    }, [token, viaLink]);

    useEffect(() => { document.title = company ? `${company.name} — ${t('intake.title')}` : 'Soroby'; }, [company, t]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setPhone = useCallback((value) => setForm((f) => ({ ...f, phone: value ?? '' })), []);
    const onToken = useCallback((v) => setTurnstileToken(v || ''), []);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (phoneValid === false) { setError(t('intake.phoneInvalid')); return; }
        if (TURNSTILE_SITE_KEY && !turnstileToken) { setError(t('intake.verify')); return; }
        setSending(true);
        try {
            const send = viaLink ? submitIntakeViaLink : submitIntake;
            const payload = TURNSTILE_SITE_KEY ? { ...form, turnstileToken } : form;
            const res = await send(token, payload);
            if (res?.code === 200) setDone(true);
            else if (res?.code === 429) setError(t('intake.tooMany'));
            else if (res?.code === 404 || res?.code === 410) setProblem(viaLink ? linkProblemKind(res?.message) : 'invalid');
            else if (res?.code === 400 && /verification/i.test(String(res?.message))) { setError(t('intake.verifyFailed')); setTurnstileReset((n) => n + 1); }
            else { setError(typeof res?.message === 'string' ? res.message : t('intake.error')); setTurnstileReset((n) => n + 1); }
        } catch {
            setError(t('intake.error'));
            setTurnstileReset((n) => n + 1);
        } finally {
            setSending(false);
        }
    };

    const problemText = problem && {
        invalid: [t('intake.invalidTitle'), t('intake.invalid')],
        expired: [t('intake.expiredTitle'), t('intake.expired')],
        used: [t('intake.usedTitle'), t('intake.used')],
        revoked: [t('intake.revokedTitle'), t('intake.revoked')],
    }[problem];

    return (
        <div className="pi">
            <style>{styles}</style>
            <div className="wrap">
                <div className="head">
                    {company?.logoUrl && <img src={company.logoUrl} alt="" />}
                    <div>
                        <div className="co">{company?.name || 'Soroby'}</div>
                        <h1>{t('intake.title')}</h1>
                    </div>
                    <div className="lang">
                        {SUPPORTED_LANGUAGES.map(({ code }) => (
                            <button type="button" key={code} className={i18n.language === code ? 'on' : ''} onClick={() => i18n.changeLanguage(code)}>{code.toUpperCase()}</button>
                        ))}
                    </div>
                </div>

                {problemText && (
                    <div className="card done">
                        <h2>{problemText[0]}</h2>
                        <p>{problemText[1]}</p>
                    </div>
                )}

                {!problem && done && (
                    <div className="card done">
                        <div className="ok"><svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg></div>
                        <h2>{t('intake.doneTitle')}</h2>
                        <p>{t('intake.done')}</p>
                    </div>
                )}

                {!problem && !done && company && (
                    <>
                        <p className="intro">{t('intake.intro', { company: company.name })}</p>
                        <form className="card" onSubmit={submit} noValidate={false}>
                            <div className="seg">
                                <button type="button" className={form.clientType === 'INDIVIDUAL' ? 'on' : ''} onClick={() => setForm(f => ({ ...f, clientType: 'INDIVIDUAL' }))}>{t('intake.person')}</button>
                                <button type="button" className={form.clientType === 'COMPANY' ? 'on' : ''} onClick={() => setForm(f => ({ ...f, clientType: 'COMPANY' }))}>{t('intake.company')}</button>
                            </div>

                            <div className="grid">
                                {form.clientType === 'COMPANY' && <Field k="companyName" value={form.companyName} onChange={set} optionalText={t('intake.optional')} label={t('intake.companyName')} required full />}
                                <Field k="firstName" value={form.firstName} onChange={set} optionalText={t('intake.optional')} label={t('intake.firstName')} required />
                                <Field k="lastName" value={form.lastName} onChange={set} optionalText={t('intake.optional')} label={t('intake.lastName')} required />
                                <div className="phone-cell">
                                    <PhoneInput
                                        name="phone"
                                        className="pi-phone"
                                        label={t('intake.phone')}
                                        required
                                        value={form.phone}
                                        onChange={setPhone}
                                        placeholder={t('intake.phoneHint')}
                                        defaultCountry={company.phoneCountry || 'RO'}
                                        onValidityChange={setPhoneValid}
                                    />
                                </div>
                                <Field k="email" value={form.email} onChange={set} optionalText={t('intake.optional')} label={t('intake.email')} type="email" optional />
                            </div>

                            <div className="sec">{t('intake.addressSection')}</div>
                            <div className="grid">
                                <Field k="street" value={form.street} onChange={set} optionalText={t('intake.optional')} label={t('intake.street')} required full />
                                <Field k="city" value={form.city} onChange={set} optionalText={t('intake.optional')} label={t('intake.city')} required />
                                <Field k="state" value={form.state} onChange={set} optionalText={t('intake.optional')} label={t('intake.state')} required />
                                <Field k="zipCode" value={form.zipCode} onChange={set} optionalText={t('intake.optional')} label={t('intake.zipCode')} required />
                                <Field k="country" value={form.country} onChange={set} optionalText={t('intake.optional')} label={t('intake.country')} required />
                                <Field k="apartmentNo" value={form.apartmentNo} onChange={set} optionalText={t('intake.optional')} label={t('intake.apartmentNo')} optional />
                                <Field k="floor" value={form.floor} onChange={set} optionalText={t('intake.optional')} label={t('intake.floor')} optional />
                                <Field k="intercomName" value={form.intercomName} onChange={set} optionalText={t('intake.optional')} label={t('intake.intercomName')} optional full />
                                <Field k="notes" value={form.notes} onChange={set} optionalText={t('intake.optional')} label={t('intake.notes')} optional full as="textarea" />
                            </div>

                            {/* Honeypot — invisible to people, filled by bots. A filled
                                value makes the server discard the submission silently, so
                                anything that fills it by mistake costs a real customer their
                                enquiry: keep it display:none and opted out of every password
                                manager we can name. */}
                            <div className="hp" aria-hidden="true">
                                <label>Website<input
                                    type="text"
                                    value={form.website}
                                    onChange={set('website')}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    data-lpignore="true"
                                    data-1p-ignore="true"
                                    data-bwignore="true"
                                    data-form-type="other"
                                /></label>
                            </div>

                            <Turnstile lang={i18n.language} onToken={onToken} resetKey={turnstileReset} />

                            {error && <div className="err">{error}</div>}
                            <button className="btn" type="submit" disabled={sending}>{sending ? t('intake.sending') : t('intake.submit')}</button>
                            <div className="privacy">{t('intake.privacy')}</div>
                        </form>
                    </>
                )}

                {!problem && !done && !company && (
                    <div className="card" style={{ color: 'var(--muted)', textAlign: 'center' }}>…</div>
                )}
            </div>
        </div>
    );
};

export default PublicIntakePage;
